import { Component, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TemplateService } from '../../../../../../core/src/lib/template.service';
import {
  MessageTemplate,
  GetMessageTemplateRequestHydrationOptionsHydrationEntityEntityType,
  GetMessageTemplateRequestHydrationOptionsInterface,
  ParticipantType,
  Participant,
} from '@vendasta/conversation';
import { Scrollable } from '@vendasta/rx-utils';
import { BehaviorSubject, catchError, firstValueFrom, map, of } from 'rxjs';
import { TEMPLATES_PAGE_SIZE } from '../../../../../../core/src/lib/inbox.constants';
import { InboxService } from '../../../../../../core/src/lib/inbox.service';
import { ConversationService } from '../../../../../../core/src/lib/state/conversation.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  PARTNER_ID_TOKEN,
} from '../../../../../../core/src/lib/tokens';
import { toSignal } from '@angular/core/rxjs-interop';
import { INBOX_TEMPLATES_ROUTER_LINK } from '../../../../../src/constants';
import { GetMessageTemplateRequestHydrationOptionsHydrationEntityInterface } from '@vendasta/conversation/lib/_internal/interfaces/api.interface';
import { Router } from '@angular/router';
import { toFirestoreId } from '../../../../../../core/src/lib/conversation-utils';

@Component({
  selector: 'inbox-insert-template',
  templateUrl: './inbox-insert-template.component.html',
  styleUrls: ['./inbox-insert-template.component.scss'],
  standalone: false,
})
export class InboxInsertTemplateComponent {
  private scrollable: Scrollable<MessageTemplate, null> = this.getScrollableList();
  protected readonly templates$ = this.scrollable.items$;

  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly loading$ = this.loading$$.asObservable();

  private readonly templateService = inject(TemplateService);
  private readonly inboxService = inject(InboxService);
  private readonly conversationService = inject(ConversationService);
  readonly isBusinessApp = this.inboxService.isBusinessApp;
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly router = inject(Router);
  readonly currentConversationDetail = toSignal(this.conversationService.currentConversationDetail$);

  constructor(private readonly dialogRef: MatDialogRef<InboxInsertTemplateComponent>) {}

  redirectEditTemplatePage(): void {
    const conversationFirestoreId = toFirestoreId(this.currentConversationDetail()?.conversation?.conversationId);

    if (this.isBusinessApp) {
      this.router.navigate([`/account/location/${this.accountGroupId()}/settings/message-templates`], {
        queryParams: {
          returnUrl: `/account/location/${this.accountGroupId()}/inbox/channel/${conversationFirestoreId}`,
        },
      });
    } else {
      this.router.navigate(INBOX_TEMPLATES_ROUTER_LINK, {
        queryParams: {
          conversationId: conversationFirestoreId,
        },
      });
    }
  }

  async selectTemplate(template: MessageTemplate): Promise<void> {
    const conversationDetail = await firstValueFrom(this.conversationService.currentConversationDetail$);
    const recipients = this.hostAppInterface.getRecipient(conversationDetail);

    const hydrationOptions: GetMessageTemplateRequestHydrationOptionsInterface = {
      hydrationEntities: this.recipientsToHydrationOptions(recipients),
    };

    if (this.isBusinessApp) {
      hydrationOptions.hydrationEntities.push({
        entityType: GetMessageTemplateRequestHydrationOptionsHydrationEntityEntityType.ENTITY_TYPE_ACCOUNT_GROUP,
        entityId: this.accountGroupId(),
      });
    } else {
      hydrationOptions.hydrationEntities.push({
        entityType: GetMessageTemplateRequestHydrationOptionsHydrationEntityEntityType.ENTITY_TYPE_PARTNER,
        entityId: this.partnerId(),
      });
    }

    const hydratedTemplate = await firstValueFrom(
      this.templateService.getTemplate(template.templateId, hydrationOptions),
    );

    this.dialogRef.close(hydratedTemplate);
  }

  convertRecipientType(
    recipientType: ParticipantType,
  ): GetMessageTemplateRequestHydrationOptionsHydrationEntityEntityType {
    switch (recipientType) {
      case ParticipantType.PARTICIPANT_TYPE_PARTNER: {
        return GetMessageTemplateRequestHydrationOptionsHydrationEntityEntityType.ENTITY_TYPE_PARTNER;
      }
      case ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP: {
        return GetMessageTemplateRequestHydrationOptionsHydrationEntityEntityType.ENTITY_TYPE_ACCOUNT_GROUP;
      }
      case ParticipantType.PARTICIPANT_TYPE_CUSTOMER: {
        return GetMessageTemplateRequestHydrationOptionsHydrationEntityEntityType.ENTITY_TYPE_CONTACT;
      }
      default: {
        return GetMessageTemplateRequestHydrationOptionsHydrationEntityEntityType.ENTITY_TYPE_UNDEFINED;
      }
    }
  }

  recipientsToHydrationOptions(
    recipients: Participant | Participant[],
  ): GetMessageTemplateRequestHydrationOptionsHydrationEntityInterface[] {
    const result: GetMessageTemplateRequestHydrationOptionsHydrationEntityInterface[] = [];
    if (Array.isArray(recipients)) {
      for (let i = 0; i < recipients.length; i++) {
        result.push({
          entityType: this.convertRecipientType(recipients[i]?.participantType),
          entityId: recipients[i]?.internalParticipantId,
        });
      }
    } else {
      result.push({
        entityType: this.convertRecipientType(recipients.participantType),
        entityId: recipients.internalParticipantId,
      });
    }

    return result;
  }

  loadNext(): void {
    this.scrollable.loadMore();
  }

  private getScrollableList() {
    return new Scrollable<MessageTemplate, null>(of(null), (_, cursor) => {
      return this.loadMore(cursor);
    });
  }

  loadMore(cursor: string) {
    return this.templateService.listTemplates(cursor, TEMPLATES_PAGE_SIZE).pipe(
      map((response) => {
        this.loading$$.next(false);
        return {
          items: response.templates,
          cursor: response.pagingMetadata?.nextCursor ?? '',
          hasMore: response.pagingMetadata?.hasMore ?? false,
        };
      }),
      catchError(() => of({ items: [] as Array<MessageTemplate>, cursor: '', hasMore: false })),
    );
  }
}
